import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import UserContext from "context/UserContext";

import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";

import { error } from "utils";

type AggregateCount = {
  key: string;
  count: number;
};

type CurrencyAggregate = {
  currency: string;
  count: number;
  saleAmount: number;
  payoutAmount: number;
};

type AffiliateSummaryResponse = {
  trackedLinks: number;
  clicks: number;
  conversions: number;
  pendingConversions: number;
  approvedConversions: number;
  lockedConversions: number;
  eligibleConversions: number;
  reversedConversions: number;
  totalSaleAmount: number;
  totalPayoutAmount: number;
  eligiblePayoutAmount: number;
  lastConversionAt?: string;
  lastTrackedLinkAt?: string;
  lastClickAt?: string;
  topSourceTools: AggregateCount[];
  topCampaigns: AggregateCount[];
  topCurrencies: CurrencyAggregate[];
};

type ConversionRow = {
  impactActionId: string;
  status: string;
  saleAmount?: number;
  payoutAmount?: number;
  currency?: string;
  impactSubId1?: string;
  impactSubId2?: string;
  impactSubId3?: string;
  actionOccurredAt?: string;
  actionLockedAt?: string;
  sourceTool?: string;
  campaign?: string;
  cartId?: string;
  trackedUrl?: string;
  destinationUrl?: string;
  isPayoutEligible: boolean;
  // Backend-supplied payout linkage. Set when this conversion has been
  // included in a payout ledger row, so the UI can show the actual end-to-end
  // status (Submitted/Paid/etc.) on the conversion row instead of an
  // ambiguous "Eligible" label.
  affiliatePayoutId?: string;
  payoutStatus?: string;
  payoutPeriodStart?: string;
  payoutPeriodEnd?: string;
  stripePayoutId?: string;
  created: string;
  updated: string;
};

type LinkRow = {
  affiliateLinkId: string;
  sourceTool?: string;
  campaign?: string;
  cartId?: string;
  trackedUrl?: string;
  destinationUrl?: string;
  impactSubId1?: string;
  impactSubId2?: string;
  impactSubId3?: string;
  clickCount: number;
  conversionCount: number;
  totalSaleAmount: number;
  totalPayoutAmount: number;
  lastClickAt?: string;
  lastConversionAt?: string;
  created: string;
  updated: string;
};

type Pagination = {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset?: number;
};

type ConversionListResponse = {
  rows: ConversionRow[];
  pagination: Pagination;
};

type LinkListResponse = {
  rows: LinkRow[];
  pagination: Pagination;
};

type AffiliatePayoutStatusResponse = {
  stripeCustomerId?: string;
  connectAccountId?: string;
  hasConnectAccount: boolean;
  readyForPayouts: boolean;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  hasExternalBankAccount: boolean;
  detailsSubmitted: boolean;
  requirementsCurrentlyDue?: string[];
  requirementsPendingVerification?: string[];
  requirementsEventuallyDue?: string[];
  lastPayoutAmount?: number;
  lastPayoutCurrency?: string;
  lastPayoutStatus?: string;
  lastPayoutAt?: number;
  pendingPayoutAmount?: number;
  pendingPayoutCurrency?: string;
};

type AffiliatePayoutLinkResponse = {
  url: string;
};

type AffiliatePayoutLedgerRow = {
  affiliatePayoutId: string;
  customerId: string;
  affiliateName: string;
  connectAccountId?: string;
  periodStart: string;
  periodEnd: string;
  payoutCurrency: string;
  grossSaleAmount: number;
  grossPayoutAmount: number;
  adjustmentAmount: number;
  netPayoutAmount: number;
  sourceConversionCount: number;
  payoutStatus: string;
  stripePayoutId?: string;
  notes?: string;
  createdBy?: string;
  created: string;
  updated: string;
};

type AffiliatePayoutLedgerListResponse = {
  rows: AffiliatePayoutLedgerRow[];
  pagination: Pagination;
};

type CustomerOption = {
  id: string;
  name: string;
  ownerEmail: string;
};

type CustomerOptionsResponse = {
  customers: CustomerOption[];
  total: number;
};

type AdminAffiliateOverviewRow = {
  customerId: string;
  customerName: string;
  ownerEmail: string;
  trackedLinks: number;
  clicks: number;
  conversions: number;
  approvedConversions: number;
  pendingConversions: number;
  totalSaleAmount: number;
  totalPayoutAmount: number;
  lastConversionAt?: string;
  lastLinkAt?: string;
};

type AdminAffiliateOverviewResponse = {
  rows: AdminAffiliateOverviewRow[];
  pagination: Pagination;
};

type AdminAffiliateUnmatchedActionRow = {
  impactActionId: string;
  affiliateName?: string;
  status?: string;
  currency?: string;
  saleAmount?: number;
  payoutAmount?: number;
  impactSubId1?: string;
  impactSubId2?: string;
  impactSubId3?: string;
  passioCustomerId?: string;
  actionOccurredAt?: string;
  actionLockedAt?: string;
  mismatchReason?: string;
  created: string;
  updated: string;
};

type AdminAffiliateUnmatchedActionListResponse = {
  rows: AdminAffiliateUnmatchedActionRow[];
  pagination: Pagination;
};

type StripeErrorDetail = {
  type?: string;
  code?: string;
  declineCode?: string;
  message?: string;
  param?: string;
  httpStatusCode?: number;
  requestId?: string;
  docUrl?: string;
  requestLogUrl?: string;
};

type AdminSettlementCurrencyRow = {
  currency?: string;
  conversionCount?: number;
  grossPayoutAmount?: number;
  grossSaleAmount?: number;
  stripeTransferId?: string;
  ledgerPayoutId?: string;
  status: string;
  reason?: string;
  stripeError?: StripeErrorDetail;
};

type AdminSettlementCustomerRow = {
  customerId: string;
  connectAccountId: string;
  rows: AdminSettlementCurrencyRow[];
};

type AdminSettlementResponse = {
  processedAt: string;
  dryRun: boolean;
  customers: AdminSettlementCustomerRow[];
};

const LIMIT = 20;

const RESTRICTED_AFFILIATE_ADMIN_PATTERNS = (
  process.env.REACT_APP_ROUTE_INSIGHTS_RESTRICTED_ADMINS || "ae.branton9*"
)
  .split(",")
  .map((value) => value.trim().toLowerCase())
  .filter((value) => value.length > 0);

const emailMatchesPattern = (email: string, pattern: string): boolean => {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPattern = pattern.trim().toLowerCase();
  if (!normalizedEmail || !normalizedPattern) return false;
  if (normalizedPattern.endsWith("*")) {
    return normalizedEmail.startsWith(normalizedPattern.slice(0, -1));
  }
  return normalizedEmail === normalizedPattern;
};

const fmtDate = (value?: string): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const fmtMoney = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  }).format(value);
};

const fmtStripeMinorMoney = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "-";
  return fmtMoney(value / 100, currency);
};

const toTitle = (value?: string): string => {
  if (!value) return "Unknown";
  return value
    .replace(/[-_]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// Affiliate-link IDs and customer IDs are full UUIDs which look like noise in a
// dense table cell. Show the first 8 hex chars (one UUID time-low segment) and
// a trailing ellipsis; the full UUID is preserved in `title` tooltips so it's
// still copyable.
const truncateUuid = (value?: string): string => {
  if (!value) return "-";
  const trimmed = value.trim();
  if (trimmed.length <= 10) return trimmed;
  return `${trimmed.slice(0, 8)}…`;
};

// Render an attribution-filter pill value compactly: source / campaign chips
// stay readable in full, but `link` chips would otherwise blow up to a 36-char
// UUID. The full value lives in the parent button's `title` for tooltip.
const formatAttributionFilterValue = (filter: {
  type: "source" | "campaign" | "link";
  value: string;
}): string => {
  if (filter.type === "link") return truncateUuid(filter.value);
  return filter.value;
};

// Customer-facing labels and copy for each payout status value. The same
// content is used in three places — the colored pill in the ledger table, the
// hover tooltip on that pill, and the expandable "How your payouts work" card
// below the ledger — so wording stays consistent and updates land everywhere.
//
// Tone here is intentionally plain-language for affiliates reading their own
// dashboard. Avoid internal terms like "ledger row" or "settlement run"; talk
// in terms of what the user cares about: "your earnings", "your bank", "when
// you'll get paid".
type PayoutStatusKey =
  | "draft"
  | "submitted"
  | "paid"
  | "failed"
  | "warning"
  | "unknown";

const PAYOUT_LEDGER_STATUS_INFO: Record<
  PayoutStatusKey,
  {
    label: string;
    pillClass: string;
    tooltip: string;
    description: string;
  }
> = {
  draft: {
    label: "Draft",
    pillClass: "bg-gray-100 text-gray-700",
    tooltip:
      "Your earnings for this period are totaled and queued for the next payout run. Nothing for you to do — we send these on a regular schedule.",
    description:
      "Your earnings for this period have been totaled up and are queued for the next payout. There's nothing for you to do — payouts run automatically on a regular schedule (about every 2 days).",
  },
  submitted: {
    label: "Submitted",
    pillClass: "bg-blue-100 text-blue-800",
    tooltip:
      "Sent from Passio to your Stripe account. Stripe is now delivering it to your bank — usually 1–2 business days. We'll flip this to Paid automatically once it lands.",
    description:
      "Your earnings have left Passio and arrived in your connected Stripe account. Stripe is now delivering them to your bank — usually within 1–2 business days. We'll automatically update this to Paid as soon as Stripe confirms the deposit.",
  },
  paid: {
    label: "Paid",
    pillClass: "bg-green-100 text-green-800",
    tooltip:
      "The deposit has been delivered to your bank. Depending on your bank, it may take an additional day or two to actually appear on your statement.",
    description:
      "The money has been paid out to your bank account. You're all set! It can take an additional 1–2 days to appear on your bank statement depending on your bank.",
  },
  failed: {
    label: "Failed",
    pillClass: "bg-red-100 text-red-700",
    tooltip:
      "Something went wrong with this payout. We'll automatically retry on the next run once it's resolved — see the note for details, or reach out if it stays stuck.",
    description:
      "Something went wrong while sending this payout — see the note on the row for details. We'll automatically try again on the next payout run once it's resolved. If it stays stuck, please reach out to support.",
  },
  warning: {
    label: "Warning",
    pillClass: "bg-amber-100 text-amber-800",
    tooltip:
      "The money was sent successfully, but our records had a small hiccup. Don't worry — the funds moved and you can verify in Stripe via the link on this row.",
    description:
      "The money was sent successfully, but we hit a small issue while recording it on our side. Don't worry — the funds did move, and you can verify the actual transfer in Stripe via the link on this row. We'll reconcile this on the next payout run.",
  },
  unknown: {
    label: "Unknown",
    pillClass: "bg-gray-100 text-gray-600",
    tooltip:
      "We don't recognize this status. If you're seeing this, please reach out to support so we can take a look.",
    description:
      "We don't recognize this status value. If you're seeing this, please reach out to support so we can take a look.",
  },
};

const resolvePayoutStatusKey = (raw?: string): PayoutStatusKey => {
  const trimmed = (raw || "").trim().toLowerCase();
  if (trimmed in PAYOUT_LEDGER_STATUS_INFO) {
    return trimmed as PayoutStatusKey;
  }
  return "unknown";
};

// Instant-show hover tooltip rendered through a React Portal so it can escape
// any clipping ancestor (`overflow-hidden` Cards, `overflow-auto` scroll
// containers, etc.). z-index alone doesn't help in those cases — only
// rendering outside the clipping subtree does.
//
// Position is computed from the trigger's bounding rect on hover/focus, so
// the popup appears immediately (no native ~700ms `title=""` delay) directly
// above the trigger. `align` controls horizontal anchoring relative to the
// trigger so the tooltip doesn't slide off the page when the trigger is at
// the start or end of a row. We don't auto-flip on viewport edge — caller
// should set `align="start"` for left-edge triggers and `align="end"` for
// right-edge ones.
const TOOLTIP_GAP_PX = 6;

const InstantTooltip = ({
  label,
  body,
  children,
  align = "center",
  className = "",
}: {
  label?: string;
  body: string;
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );

  const positionTooltip = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let anchorX = rect.left + rect.width / 2;
    if (align === "start") anchorX = rect.left;
    if (align === "end") anchorX = rect.right;
    setCoords({
      top: rect.top + window.scrollY - TOOLTIP_GAP_PX,
      left: anchorX + window.scrollX,
    });
  }, [align]);

  const hide = useCallback(() => setCoords(null), []);

  // Hide the tooltip on scroll/resize since its absolute position would
  // otherwise drift relative to the trigger. Cheap and avoids stale popups.
  useEffect(() => {
    if (coords === null) return undefined;
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [coords, hide]);

  const transform =
    align === "start"
      ? "translate(0, -100%)"
      : align === "end"
      ? "translate(-100%, -100%)"
      : "translate(-50%, -100%)";

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex ${className}`}
        onMouseEnter={positionTooltip}
        onMouseLeave={hide}
        onFocus={positionTooltip}
        onBlur={hide}
      >
        {children}
      </span>
      {coords !== null &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              transform,
              zIndex: 9999,
            }}
            className="pointer-events-none w-64 max-w-xs rounded-md bg-gray-900 px-3 py-2 text-[11px] leading-snug text-white shadow-lg whitespace-normal text-left"
          >
            {label && <span className="block font-semibold mb-1">{label}</span>}
            {body}
          </div>,
          document.body
        )}
    </>
  );
};

// Status pill used everywhere in the affiliate-earnings UI that needs to
// display a payout status — table cells, the explainer card, etc. Renders
// the colored pill, and (when `tooltip` is true) attaches an instant CSS-only
// hover popup with the per-status explanation. Centralized so a future status
// addition only needs an entry in PAYOUT_LEDGER_STATUS_INFO.
const PayoutStatusBadge = ({
  statusKey,
  size = "sm",
  tooltip = true,
  align = "center",
  className = "",
}: {
  statusKey: PayoutStatusKey;
  size?: "xs" | "sm";
  tooltip?: boolean;
  align?: "start" | "center" | "end";
  className?: string;
}) => {
  const info = PAYOUT_LEDGER_STATUS_INFO[statusKey];
  const sizeClass =
    size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]";
  const pill = (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizeClass} ${
        info.pillClass
      } ${tooltip ? "cursor-help" : ""} ${className}`}
    >
      {info.label}
    </span>
  );
  if (!tooltip) return pill;
  return (
    <InstantTooltip label={info.label} body={info.tooltip} align={align}>
      {pill}
    </InstantTooltip>
  );
};

// Stripe surfaces test- vs live-mode under different URL paths in the
// dashboard. We can't reliably tell from the transfer ID alone (both modes
// use the `tr_` prefix), but the dashboard auto-prompts a mode switch when
// the URL doesn't match the active mode, so a single canonical link works for
// both. Connect transfers live under `/connect/transfers/<id>`.
const stripeConnectTransferDashboardUrl = (transferId: string): string =>
  `https://dashboard.stripe.com/connect/transfers/${encodeURIComponent(
    transferId.trim()
  )}`;

// Render the "Payout Status" cell on a Conversion Actions row. The cell
// implicitly links the conversion to the payout it was rolled into:
//
//   1. If the backend matched this conversion to a payout ledger row, show
//      the same colored pill (Submitted / Paid / etc.) that appears on that
//      ledger row, with a tooltip naming the payout date so the user can
//      cross-reference. This collapses the previously-confusing situation of
//      a conversion saying "Eligible" while its money was already in a
//      Submitted payout.
//   2. Otherwise, if the conversion is approved + locked but no payout has
//      picked it up yet, show "Eligible" — meaning "ready, going out next run".
//   3. Otherwise, "Waiting" — Impact hasn't approved/locked it yet.
//
// All three branches use InstantTooltip so hover behavior is consistent
// across the whole table.
const renderConversionPayoutStatus = (row: ConversionRow) => {
  const payoutStatus = (row.payoutStatus || "").trim().toLowerCase();
  if (payoutStatus) {
    const statusKey = resolvePayoutStatusKey(payoutStatus);
    const statusInfo = PAYOUT_LEDGER_STATUS_INFO[statusKey];
    const periodLabel = row.payoutPeriodEnd
      ? `your ${fmtShortDate(row.payoutPeriodEnd)} payout`
      : "a payout";
    return (
      <InstantTooltip
        align="start"
        label={`Included in ${periodLabel}`}
        body={statusInfo.tooltip}
      >
        <PayoutStatusBadge
          statusKey={statusKey}
          align="start"
          tooltip={false}
        />
      </InstantTooltip>
    );
  }
  if (row.isPayoutEligible) {
    return (
      <InstantTooltip
        align="start"
        label="Eligible"
        body="This conversion is approved and ready to be paid out. We'll include it in the next payout (about every 2 days)."
      >
        <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 cursor-help">
          Eligible
        </span>
      </InstantTooltip>
    );
  }
  return (
    <InstantTooltip
      align="start"
      label="Waiting"
      body="This conversion is still being reviewed (or hasn't passed the lock window yet). Once Impact marks it approved and the lock period ends, it'll move to Eligible and roll into the next payout."
    >
      <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 cursor-help">
        Waiting
      </span>
    </InstantTooltip>
  );
};

// Compact date formatter (no time-of-day) for tooltips that only need a
// human-readable day. Falls back to "?" rather than "-" so callers can
// confidently inline it into prose.
const fmtShortDate = (value?: string): string => {
  if (!value) return "?";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "?";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Short, actionable hints for known Stripe error codes encountered during
// payout settlement. The raw `message` from Stripe is preserved verbatim in
// the expandable details pane; this map is just to give an admin a one-line
// "what to do next" without requiring them to click into the doc URL. Add
// codes here as we observe new failure modes in production / staging.
const STRIPE_ERROR_HINTS: Record<string, string> = {
  balance_insufficient:
    "The platform Stripe account doesn't have enough available USD to cover this transfer. In test mode, fund the platform balance with the 4000000000000077 test card; in live mode this means waiting for incoming charges to clear before settlement runs.",
  account_invalid:
    "Stripe rejected the destination Connect account. The account may have been disconnected, restricted, or never finished onboarding.",
  amount_too_small:
    "The payout amount is below Stripe's minimum transfer threshold for this currency. Wait for additional eligible conversions to accumulate or adjust the settlement floor.",
  charge_disabled:
    "The destination Connect account has charges disabled and can't receive transfers. Have the affiliate complete any outstanding Stripe identity / requirements.",
  payouts_disabled:
    "Stripe has disabled payouts on the destination Connect account, usually due to missing verification info. Have the affiliate finish setup in their Stripe Express dashboard.",
  bank_account_unusable:
    "The destination Connect account's bank account is not usable. The affiliate needs to add or repair their bank account in Stripe before settlements can land.",
};

// Friendly labels for Stripe Connect Express requirement codes. The API returns
// raw paths like `business_profile.url` / `tos_acceptance.date` which are not
// useful to surface directly to a customer. Anything missing from this map
// falls through to a generic prettifier (last segment + capitalize words).
const STRIPE_REQUIREMENT_LABELS: Record<string, string> = {
  "business_profile.url": "Business website",
  "business_profile.mcc": "Business industry",
  "business_profile.product_description": "Product description",
  "business_profile.support_phone": "Support phone number",
  "business_profile.support_email": "Support email",
  "business_profile.name": "Business name",
  business_type: "Business type",

  "tos_acceptance.date": "Accept Stripe terms of service",
  "tos_acceptance.ip": "Accept Stripe terms of service",

  external_account: "Bank account or debit card",

  "individual.first_name": "Legal first name",
  "individual.last_name": "Legal last name",
  "individual.email": "Email address",
  "individual.phone": "Phone number",
  "individual.dob.day": "Date of birth",
  "individual.dob.month": "Date of birth",
  "individual.dob.year": "Date of birth",
  "individual.id_number": "Government ID (full SSN)",
  "individual.ssn_last_4": "Last 4 of SSN",
  "individual.address.line1": "Home address",
  "individual.address.city": "Home address",
  "individual.address.state": "Home address",
  "individual.address.postal_code": "Home address",
  "individual.address.country": "Home country",
  "individual.verification.document": "Government-issued ID document",
  "individual.verification.additional_document": "Additional ID document",
  "individual.political_exposure": "Political exposure disclosure",

  "company.name": "Company name",
  "company.tax_id": "Tax ID (EIN)",
  "company.phone": "Company phone",
  "company.address.line1": "Company address",
  "company.address.city": "Company address",
  "company.address.state": "Company address",
  "company.address.postal_code": "Company address",
  "company.address.country": "Company country",
  "company.directors_provided": "Directors information",
  "company.executives_provided": "Executives information",
  "company.owners_provided": "Owners information",
  "company.verification.document": "Company verification document",

  "relationship.representative": "Account representative",
  "relationship.owner": "Account owner",
  "relationship.director": "Director",
  "relationship.executive": "Executive",

  "settings.payouts.statement_descriptor": "Bank statement descriptor",
};

const friendlyStripeRequirement = (code: string): string => {
  const direct = STRIPE_REQUIREMENT_LABELS[code];
  if (direct) return direct;
  const last = (code.split(".").pop() || code).replace(/^_+/, "");
  return last.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const dedupeFriendlyStripeRequirements = (
  codes: string[] | undefined
): string[] => {
  if (!codes?.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of codes) {
    const label = friendlyStripeRequirement(code);
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
};

// Mirrors the env detection used elsewhere in the portal (mealPlanApiClient,
// DetailsPassioHub, DetailsNutritionPlatform). Picks the dev passiolife API
// host when running against any non-prod environment.
const isPassioDevEnv = (): boolean =>
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENV === "development" ||
  (typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname.includes("-dev")));

const passioApiBaseUrl = (): string =>
  isPassioDevEnv()
    ? "https://api-dev.passiolife.com"
    : "https://api.passiolife.com";

// The link generator UI deliberately only surfaces the N-API surface — that's
// the route external integrators are expected to call from their backends.
// `customerId` is omitted from inputs because the N-API token already carries
// customer context, so the server resolves it automatically.
const AFFILIATE_LINK_NAPI_PATH =
  "/v2/products/napi/affiliates/convertShoppingListToAffiliateLink";

const buildAffiliateLinkRequestUrl = (params: {
  sourceTool?: string;
  cartId?: string;
  campaign?: string;
}): string => {
  const base = `${passioApiBaseUrl()}${AFFILIATE_LINK_NAPI_PATH}`;
  const search = new URLSearchParams();
  if (params.sourceTool?.trim())
    search.set("sourceTool", params.sourceTool.trim());
  if (params.cartId?.trim()) search.set("cartId", params.cartId.trim());
  if (params.campaign?.trim()) search.set("campaign", params.campaign.trim());
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
};

const buildAffiliateLinkCurl = (params: {
  sourceTool?: string;
  cartId?: string;
  campaign?: string;
}): string => {
  const url = buildAffiliateLinkRequestUrl(params);
  return [
    "curl --fail-with-body --silent --show-error \\",
    "  -X POST \\",
    '  -H "Authorization: Bearer ${NAPI_TOKEN}" \\',
    '  -H "Content-Type: application/json" \\',
    `  "${url}" \\`,
    "  -d '[",
    '    {"name":"Bananas","portionQuantity":6,"portionSize":"count"},',
    '    {"name":"Greek Yogurt","portionQuantity":2,"portionSize":"container"}',
    "  ]'",
  ].join("\n");
};

type CopyButtonProps = {
  value: string;
  label?: string;
  className?: string;
  iconOnly?: boolean;
};

const CopyButton = ({
  value,
  label = "Copy",
  className = "",
  iconOnly = false,
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard errors silently; visual state just won't update
    }
  }, [value]);

  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={!value}
      className={`inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      title={copied ? "Copied!" : label}
    >
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
      )}
      {!iconOnly && <span>{copied ? "Copied" : label}</span>}
    </button>
  );
};

// Compact, two-tier rendering for a Stripe API error returned by the payout
// settlement endpoint. Tier 1 (always visible) is a one-line summary
// (`code: trimmed message`) plus a tailored "Next steps" hint when we have a
// known fix for that error code. Tier 2 (collapsed by default behind a
// `<details>` toggle) carries the full structured fields — type, request id,
// HTTP status, doc URL, request log URL — so an admin can dig into the
// underlying Stripe call without leaving the page. Raw JSON dumps stay out of
// the table cell entirely.
const SettlementStripeErrorDetail = ({
  error,
  fallbackReason,
}: {
  error: StripeErrorDetail;
  fallbackReason?: string;
}) => {
  const code = (error.code || "").trim();
  const summaryParts: string[] = [];
  if (code) summaryParts.push(code);
  const fullMessage = (error.message || "").trim();
  if (fullMessage) {
    const sentenceEnd = fullMessage.search(/[.!\n]/);
    const summaryMessage =
      sentenceEnd > 0 && sentenceEnd < fullMessage.length - 1
        ? fullMessage.slice(0, sentenceEnd + 1)
        : fullMessage.length > 200
        ? `${fullMessage.slice(0, 197)}...`
        : fullMessage;
    summaryParts.push(summaryMessage);
  }
  const summary = summaryParts.length > 0 ? summaryParts.join(": ") : "";
  const hint = code ? STRIPE_ERROR_HINTS[code] : undefined;
  const hasMoreToShow = Boolean(
    (fullMessage && fullMessage.length > (summary ? summary.length : 0)) ||
      error.requestId ||
      error.docUrl ||
      error.requestLogUrl ||
      error.type ||
      error.httpStatusCode ||
      error.param
  );

  return (
    <div className="mt-1 max-w-[640px]">
      <div className="text-[11px] text-red-700">
        {summary || fallbackReason || "Stripe API error"}
      </div>
      {hint && (
        <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] leading-snug text-amber-900">
          <span className="font-semibold">Next steps:</span> {hint}
        </div>
      )}
      {hasMoreToShow && (
        <details className="mt-1 group">
          <summary className="cursor-pointer text-[10px] font-medium text-gray-600 hover:text-gray-900 select-none">
            Show details
          </summary>
          <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-2 text-[10px] leading-relaxed text-gray-700">
            {fullMessage && fullMessage !== summary && (
              <p className="mb-1.5 whitespace-pre-wrap break-words">
                {fullMessage}
              </p>
            )}
            <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5">
              {error.type && (
                <>
                  <dt className="font-semibold text-gray-500">Type</dt>
                  <dd className="font-mono">{error.type}</dd>
                </>
              )}
              {code && (
                <>
                  <dt className="font-semibold text-gray-500">Code</dt>
                  <dd className="font-mono">{code}</dd>
                </>
              )}
              {error.declineCode && (
                <>
                  <dt className="font-semibold text-gray-500">Decline</dt>
                  <dd className="font-mono">{error.declineCode}</dd>
                </>
              )}
              {error.httpStatusCode ? (
                <>
                  <dt className="font-semibold text-gray-500">HTTP</dt>
                  <dd className="font-mono">{error.httpStatusCode}</dd>
                </>
              ) : null}
              {error.param && (
                <>
                  <dt className="font-semibold text-gray-500">Param</dt>
                  <dd className="font-mono">{error.param}</dd>
                </>
              )}
              {error.requestId && (
                <>
                  <dt className="font-semibold text-gray-500">Request</dt>
                  <dd className="font-mono">{error.requestId}</dd>
                </>
              )}
            </dl>
            {(error.docUrl || error.requestLogUrl) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {error.docUrl && (
                  <a
                    href={error.docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-700 hover:underline"
                  >
                    Stripe docs ↗
                  </a>
                )}
                {error.requestLogUrl && (
                  <a
                    href={error.requestLogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-700 hover:underline"
                  >
                    Request log ↗
                  </a>
                )}
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

const AffiliateEarnings = () => {
  const userContext = useContext(UserContext);
  const [conversionSortBy, setConversionSortBy] =
    useState<string>("actionOccurredAt");
  const [conversionSortOrder, setConversionSortOrder] = useState<
    "asc" | "desc"
  >("desc");
  const [conversionOffset, setConversionOffset] = useState(0);
  const [conversionStatus, setConversionStatus] = useState("");
  const [conversionSearchInput, setConversionSearchInput] = useState("");
  const [conversionSearch, setConversionSearch] = useState("");

  const [linkSortBy, setLinkSortBy] = useState<string>("created");
  const [linkSortOrder, setLinkSortOrder] = useState<"asc" | "desc">("desc");
  const [linkOffset, setLinkOffset] = useState(0);
  const [linkSearchInput, setLinkSearchInput] = useState("");
  const [linkSearch, setLinkSearch] = useState("");
  const [payoutCtaLoading, setPayoutCtaLoading] = useState<
    "onboarding" | "dashboard" | null
  >(null);
  const [affiliateViewMode, setAffiliateViewMode] = useState<
    "customer" | "admin"
  >("customer");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerPickerSearchInput, setCustomerPickerSearchInput] =
    useState("");
  const [adminSearchInput, setAdminSearchInput] = useState("");
  const [adminSearch, setAdminSearch] = useState("");
  const [adminSortBy, setAdminSortBy] = useState("totalPayoutAmount");
  const [adminSortOrder, setAdminSortOrder] = useState<"asc" | "desc">("desc");
  const [adminOffset, setAdminOffset] = useState(0);
  const [unmatchedSearchInput, setUnmatchedSearchInput] = useState("");
  const [unmatchedSearch, setUnmatchedSearch] = useState("");
  const [unmatchedOffset, setUnmatchedOffset] = useState(0);
  const [reconcileActionId, setReconcileActionId] = useState("");
  const [reconcileAffiliateLinkId, setReconcileAffiliateLinkId] = useState("");
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [adminSettlementLoading, setAdminSettlementLoading] = useState<
    "dryRun" | "run" | null
  >(null);
  const [adminSettlementResult, setAdminSettlementResult] =
    useState<AdminSettlementResponse | null>(null);
  const [hideSkippedSettlementRows, setHideSkippedSettlementRows] =
    useState(true);

  const [docsExpanded, setDocsExpanded] = useState(false);
  const [builderSourceTool, setBuilderSourceTool] = useState("");
  const [builderCartId, setBuilderCartId] = useState("");
  const [builderCampaign, setBuilderCampaign] = useState("");

  const [activeAttributionFilter, setActiveAttributionFilter] = useState<{
    type: "source" | "campaign" | "link";
    value: string;
  } | null>(null);

  // Pushing the filter into both the conversion and link search inputs lets us
  // reuse the existing server-side `search` filter (which already matches
  // sourceTool / campaign / cart / subId across both tables) instead of adding
  // a parallel filter pipeline.
  const applyAttributionFilter = useCallback(
    (type: "source" | "campaign" | "link", value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      setActiveAttributionFilter({ type, value: trimmed });
      setConversionSearchInput(trimmed);
      setLinkSearchInput(trimmed);
      setConversionOffset(0);
      setLinkOffset(0);
    },
    []
  );

  const clearAttributionFilter = useCallback(() => {
    setActiveAttributionFilter(null);
    setConversionSearchInput("");
    setLinkSearchInput("");
    setConversionOffset(0);
    setLinkOffset(0);
  }, []);

  const builderUrl = useMemo(
    () =>
      buildAffiliateLinkRequestUrl({
        sourceTool: builderSourceTool,
        cartId: builderCartId,
        campaign: builderCampaign,
      }),
    [builderSourceTool, builderCartId, builderCampaign]
  );

  const builderCurl = useMemo(
    () =>
      buildAffiliateLinkCurl({
        sourceTool: builderSourceTool,
        cartId: builderCartId,
        campaign: builderCampaign,
      }),
    [builderSourceTool, builderCartId, builderCampaign]
  );

  const canViewAffiliateAdminDashboard = useMemo(() => {
    const email = (userContext?.email || "").toLowerCase();
    return RESTRICTED_AFFILIATE_ADMIN_PATTERNS.some((pattern) =>
      emailMatchesPattern(email, pattern)
    );
  }, [userContext?.email]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setConversionSearch(conversionSearchInput.trim());
      setConversionOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [conversionSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLinkSearch(linkSearchInput.trim());
      setLinkOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [linkSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAdminSearch(adminSearchInput.trim());
      setAdminOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [adminSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUnmatchedSearch(unmatchedSearchInput.trim());
      setUnmatchedOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [unmatchedSearchInput]);

  const activeCustomerOverride = selectedCustomerId || undefined;

  const summaryQuery = useQuery(
    ["affiliateSummary"],
    async () => {
      const response = await axios.get<AffiliateSummaryResponse>(
        "/accounts/customer/affiliate-earnings/summary",
        {
          params: {
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const conversionsQuery = useQuery(
    [
      "affiliateConversions",
      conversionSortBy,
      conversionSortOrder,
      conversionOffset,
      conversionSearch,
      conversionStatus,
      activeCustomerOverride,
    ],
    async () => {
      const response = await axios.get<ConversionListResponse>(
        "/accounts/customer/affiliate-earnings/conversions",
        {
          params: {
            limit: LIMIT,
            offset: conversionOffset,
            search: conversionSearch || undefined,
            status: conversionStatus || undefined,
            sortBy: conversionSortBy,
            sortOrder: conversionSortOrder,
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const linksQuery = useQuery(
    [
      "affiliateLinks",
      linkSortBy,
      linkSortOrder,
      linkOffset,
      linkSearch,
      activeCustomerOverride,
    ],
    async () => {
      const response = await axios.get<LinkListResponse>(
        "/accounts/customer/affiliate-earnings/links",
        {
          params: {
            limit: LIMIT,
            offset: linkOffset,
            search: linkSearch || undefined,
            sortBy: linkSortBy,
            sortOrder: linkSortOrder,
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const payoutStatusQuery = useQuery(
    ["affiliatePayoutStatus", activeCustomerOverride],
    async () => {
      const response = await axios.get<AffiliatePayoutStatusResponse>(
        "/accounts/customer/affiliate-earnings/payout-status",
        {
          params: {
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const payoutLedgerQuery = useQuery(
    ["affiliatePayoutLedger", activeCustomerOverride],
    async () => {
      const response = await axios.get<AffiliatePayoutLedgerListResponse>(
        "/accounts/customer/affiliate-earnings/payout-ledger",
        {
          params: {
            limit: 10,
            sortBy: "periodEnd",
            sortOrder: "desc",
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const customerOptionsQuery = useQuery(
    ["affiliateAdminCustomers", canViewAffiliateAdminDashboard],
    async () => {
      const response = await axios.get<CustomerOptionsResponse>(
        "/accounts/products/unified/analytics/customers"
      );
      return response.data;
    },
    { enabled: canViewAffiliateAdminDashboard }
  );

  const adminOverviewQuery = useQuery(
    [
      "affiliateAdminOverview",
      canViewAffiliateAdminDashboard,
      adminSearch,
      adminSortBy,
      adminSortOrder,
      adminOffset,
    ],
    async () => {
      const response = await axios.get<AdminAffiliateOverviewResponse>(
        "/accounts/customer/affiliate-earnings/admin/overview",
        {
          params: {
            limit: LIMIT,
            offset: adminOffset,
            search: adminSearch || undefined,
            sortBy: adminSortBy,
            sortOrder: adminSortOrder,
          },
        }
      );
      return response.data;
    },
    {
      enabled: canViewAffiliateAdminDashboard,
      keepPreviousData: true,
    }
  );

  const unmatchedActionsQuery = useQuery(
    [
      "affiliateAdminUnmatchedActions",
      canViewAffiliateAdminDashboard,
      unmatchedSearch,
      unmatchedOffset,
    ],
    async () => {
      const response =
        await axios.get<AdminAffiliateUnmatchedActionListResponse>(
          "/accounts/customer/affiliate-earnings/admin/unmatched-actions",
          {
            params: {
              limit: LIMIT,
              offset: unmatchedOffset,
              search: unmatchedSearch || undefined,
            },
          }
        );
      return response.data;
    },
    {
      enabled: canViewAffiliateAdminDashboard,
      keepPreviousData: true,
    }
  );

  const summary = summaryQuery.data;
  const conversionRows = conversionsQuery.data?.rows || [];
  const linkRows = linksQuery.data?.rows || [];
  const payoutStatus = payoutStatusQuery.data;
  const payoutLedgerRows = payoutLedgerQuery.data?.rows || [];
  const unmatchedRows = unmatchedActionsQuery.data?.rows || [];
  const customerOptions = useMemo(
    () => customerOptionsQuery.data?.customers || [],
    [customerOptionsQuery.data?.customers]
  );
  const filteredCustomerOptions = useMemo(() => {
    const needle = customerPickerSearchInput.trim().toLowerCase();
    if (!needle) return customerOptions;
    return customerOptions.filter((customer) => {
      return (
        customer.id.toLowerCase().includes(needle) ||
        (customer.name || "").toLowerCase().includes(needle) ||
        (customer.ownerEmail || "").toLowerCase().includes(needle)
      );
    });
  }, [customerOptions, customerPickerSearchInput]);
  const selectedCustomerOption = useMemo(() => {
    if (!selectedCustomerId) return undefined;
    return customerOptions.find(
      (customer) => customer.id === selectedCustomerId
    );
  }, [customerOptions, selectedCustomerId]);

  const toggleConversionSort = (nextSortBy: string) => {
    if (conversionSortBy === nextSortBy) {
      setConversionSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setConversionSortBy(nextSortBy);
      setConversionSortOrder("desc");
    }
    setConversionOffset(0);
  };

  const toggleLinkSort = (nextSortBy: string) => {
    if (linkSortBy === nextSortBy) {
      setLinkSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setLinkSortBy(nextSortBy);
      setLinkSortOrder("desc");
    }
    setLinkOffset(0);
  };

  const toggleAdminSort = (nextSortBy: string) => {
    if (adminSortBy === nextSortBy) {
      setAdminSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setAdminSortBy(nextSortBy);
      setAdminSortOrder("desc");
    }
    setAdminOffset(0);
  };

  const conversionsPageLabel = useMemo(() => {
    const p = conversionsQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [conversionsQuery.data?.pagination]);

  const linksPageLabel = useMemo(() => {
    const p = linksQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [linksQuery.data?.pagination]);

  const adminPageLabel = useMemo(() => {
    const p = adminOverviewQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [adminOverviewQuery.data?.pagination]);

  const unmatchedPageLabel = useMemo(() => {
    const p = unmatchedActionsQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [unmatchedActionsQuery.data?.pagination]);

  // Best-effort lookup of customerId → name/email so the settlement table
  // doesn't surface bare UUIDs. Only the current page of admin overview rows
  // is available without an additional fetch, but for the typical ≤5
  // mappings-per-test-environment case this is enough.
  const customerNameById = useMemo(() => {
    const map = new Map<string, { name?: string; email?: string }>();
    (adminOverviewQuery.data?.rows || []).forEach((row) => {
      map.set(row.customerId, {
        name: row.customerName,
        email: row.ownerEmail,
      });
    });
    return map;
  }, [adminOverviewQuery.data?.rows]);

  // Flatten the settlement response into one row per (customer, currency)
  // pair, enriched with display state (sort priority, "you" indicator,
  // resolved name/email). We sort actionable rows first so the user's eye
  // lands on transfers that would actually execute, then "skipped: no
  // eligible activity" rows after, then errors at the bottom.
  const settlementFlatRows = useMemo(() => {
    if (!adminSettlementResult) return [];
    const myCustomerId = (userContext?.customerid || "").trim().toLowerCase();
    const flat: Array<{
      customerId: string;
      connectAccountId: string;
      customerName?: string;
      ownerEmail?: string;
      isMe: boolean;
      currency?: string;
      conversionCount?: number;
      grossPayoutAmount?: number;
      stripeTransferId?: string;
      ledgerPayoutId?: string;
      status: string;
      reason?: string;
      stripeError?: StripeErrorDetail;
      sortPriority: number;
    }> = [];
    adminSettlementResult.customers.forEach((customer) => {
      const meta = customerNameById.get(customer.customerId);
      const isMe =
        myCustomerId.length > 0 &&
        customer.customerId.trim().toLowerCase() === myCustomerId;
      (customer.rows || []).forEach((row) => {
        const status = (row.status || "").toLowerCase();
        // Lower number = higher in the table.
        const sortPriority = status.includes("error")
          ? 3
          : status === "skipped"
          ? 2
          : 1;
        flat.push({
          customerId: customer.customerId,
          connectAccountId: customer.connectAccountId,
          customerName: meta?.name,
          ownerEmail: meta?.email,
          isMe,
          currency: row.currency,
          conversionCount: row.conversionCount,
          grossPayoutAmount: row.grossPayoutAmount,
          stripeTransferId: row.stripeTransferId,
          ledgerPayoutId: row.ledgerPayoutId,
          status: row.status,
          reason: row.reason,
          stripeError: row.stripeError,
          sortPriority,
        });
      });
    });
    flat.sort((a, b) => {
      if (a.sortPriority !== b.sortPriority) {
        return a.sortPriority - b.sortPriority;
      }
      // Within the same bucket, "you" first, then alpha by customer name/id.
      if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
      const aLabel = (a.customerName || a.customerId).toLowerCase();
      const bLabel = (b.customerName || b.customerId).toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
    return flat;
  }, [adminSettlementResult, customerNameById, userContext?.customerid]);

  const settlementSummary = useMemo(() => {
    const totalsByCurrency = new Map<string, number>();
    let actionableCount = 0;
    let skippedCount = 0;
    let errorCount = 0;
    settlementFlatRows.forEach((row) => {
      const status = (row.status || "").toLowerCase();
      if (status.includes("error")) {
        errorCount += 1;
      } else if (status === "skipped") {
        skippedCount += 1;
      } else {
        actionableCount += 1;
        const currency = (row.currency || "USD").toUpperCase();
        totalsByCurrency.set(
          currency,
          (totalsByCurrency.get(currency) || 0) + (row.grossPayoutAmount || 0)
        );
      }
    });
    return {
      total: settlementFlatRows.length,
      actionableCount,
      skippedCount,
      errorCount,
      totalsByCurrency,
    };
  }, [settlementFlatRows]);

  const visibleSettlementRows = useMemo(() => {
    if (!hideSkippedSettlementRows) return settlementFlatRows;
    return settlementFlatRows.filter(
      (row) => (row.status || "").toLowerCase() !== "skipped"
    );
  }, [settlementFlatRows, hideSkippedSettlementRows]);

  const openAffiliatePayoutLink = async (mode: "onboarding" | "dashboard") => {
    try {
      setPayoutCtaLoading(mode);
      const endpoint =
        mode === "onboarding"
          ? "/accounts/customer/affiliate-earnings/payout-onboarding-link"
          : "/accounts/customer/affiliate-earnings/payout-dashboard-link";
      const response = await axios.post<AffiliatePayoutLinkResponse>(
        endpoint,
        {},
        {
          params: {
            customerId: activeCustomerOverride,
          },
        }
      );
      const url = response.data?.url;
      if (!url) {
        throw new Error("Missing redirect URL");
      }
      window.open(url, "_blank", "noopener,noreferrer");
      await payoutStatusQuery.refetch();
    } catch (err: any) {
      error(
        err?.response?.data?.cause ||
          "Could not open Stripe payout setup. Please try again."
      );
    } finally {
      setPayoutCtaLoading(null);
    }
  };

  const runAdminSettlement = async (dryRun: boolean) => {
    try {
      setAdminSettlementLoading(dryRun ? "dryRun" : "run");
      const response = await axios.post<AdminSettlementResponse>(
        "/accounts/customer/affiliate-earnings/admin/payout-settlement",
        {
          dryRun,
          confirm: !dryRun,
          customerId: selectedCustomerId || undefined,
        }
      );
      setAdminSettlementResult(response.data);
      payoutLedgerQuery.refetch();
      payoutStatusQuery.refetch();
      summaryQuery.refetch();
      conversionsQuery.refetch();
      linksQuery.refetch();
      adminOverviewQuery.refetch();
    } catch (err: any) {
      error(
        err?.response?.data?.cause ||
          "Could not run affiliate payout settlement."
      );
    } finally {
      setAdminSettlementLoading(null);
    }
  };

  const reconcileUnmatchedAction = async () => {
    if (!reconcileActionId.trim()) {
      error("Impact action ID is required.");
      return;
    }
    if (!reconcileAffiliateLinkId.trim()) {
      error("Affiliate link ID is required.");
      return;
    }
    try {
      setReconcileLoading(true);
      await axios.post(
        "/accounts/customer/affiliate-earnings/admin/reconcile-unmatched-action",
        {
          impactActionId: reconcileActionId.trim(),
          affiliateLinkId: reconcileAffiliateLinkId.trim(),
        }
      );
      setReconcileActionId("");
      unmatchedActionsQuery.refetch();
      summaryQuery.refetch();
      conversionsQuery.refetch();
      linksQuery.refetch();
      adminOverviewQuery.refetch();
      payoutLedgerQuery.refetch();
    } catch (err: any) {
      error(
        err?.response?.data?.cause ||
          "Could not reconcile unmatched affiliate action."
      );
    } finally {
      setReconcileLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {canViewAffiliateAdminDashboard && (
        <Card className="sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Affiliate Workspace
              </h3>
              <p className="text-sm text-gray-600">
                Switch between your customer-facing earnings view and the Passio
                admin dashboard.
              </p>
            </div>
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
              <button
                type="button"
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  affiliateViewMode === "customer"
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setAffiliateViewMode("customer")}
              >
                Customer View
              </button>
              <button
                type="button"
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  affiliateViewMode === "admin"
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setAffiliateViewMode("admin")}
              >
                Admin View
              </button>
            </div>
          </div>
        </Card>
      )}

      {canViewAffiliateAdminDashboard && affiliateViewMode === "admin" && (
        <Card className="sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Admin Affiliate Dashboard
              </h3>
              <p className="text-sm text-gray-500">
                Passio admin-only view across all customers with override
                controls.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                <input
                  value={customerPickerSearchInput}
                  onChange={(e) => setCustomerPickerSearchInput(e.target.value)}
                  placeholder="Search customer picker..."
                  className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md min-w-[260px]"
                />
              </div>
              <select
                value={selectedCustomerId}
                onChange={(e) => {
                  setSelectedCustomerId(e.target.value);
                  setConversionOffset(0);
                  setLinkOffset(0);
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md min-w-[260px]"
              >
                <option value="">View my customer context</option>
                {filteredCustomerOptions.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name || "Unnamed Customer"} -{" "}
                    {customer.ownerEmail || "No owner email"} - {customer.id}
                  </option>
                ))}
              </select>
              {selectedCustomerId && (
                <button
                  type="button"
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                  onClick={() => setSelectedCustomerId("")}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-medium text-indigo-900">
                  Payout Settlement Controls
                </h4>
                <p className="text-sm text-indigo-700">
                  Admin-only preview and manual execution of Stripe Connect
                  payout settlement.{" "}
                  {selectedCustomerId
                    ? "Runs only for selected customer."
                    : "Runs for all mapped customers."}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                  onClick={() => runAdminSettlement(true)}
                  disabled={adminSettlementLoading !== null}
                >
                  {adminSettlementLoading === "dryRun"
                    ? "Previewing..."
                    : "Preview payout batch"}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                  onClick={() => runAdminSettlement(false)}
                  disabled={adminSettlementLoading !== null}
                >
                  {adminSettlementLoading === "run"
                    ? "Submitting..."
                    : "Run payout settlement now"}
                </button>
              </div>
            </div>
            {adminSettlementResult && (
              <div className="mt-3 rounded-md border border-indigo-200 bg-white p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm text-gray-700">
                    Last run: {fmtDate(adminSettlementResult.processedAt)} (
                    {adminSettlementResult.dryRun ? "preview" : "live"})
                  </p>
                  <p className="text-xs text-gray-500">
                    Each row is one Stripe Connect mapping in our database.
                    Customers without eligible activity show as{" "}
                    <span className="font-medium">Skipped</span> — that&apos;s
                    expected.
                  </p>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 rounded border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900">
                  <span>
                    <span className="font-semibold">
                      {settlementSummary.actionableCount}
                    </span>{" "}
                    eligible
                  </span>
                  <span>
                    <span className="font-semibold">
                      {settlementSummary.skippedCount}
                    </span>{" "}
                    skipped
                  </span>
                  {settlementSummary.errorCount > 0 && (
                    <span className="text-red-700">
                      <span className="font-semibold">
                        {settlementSummary.errorCount}
                      </span>{" "}
                      errors
                    </span>
                  )}
                  {Array.from(settlementSummary.totalsByCurrency.entries())
                    .filter(([, amount]) => amount > 0)
                    .map(([currency, amount]) => (
                      <span key={currency}>
                        Total{" "}
                        {adminSettlementResult.dryRun
                          ? "would settle"
                          : "settled"}
                        :{" "}
                        <span className="font-semibold">
                          {fmtMoney(amount, currency)}
                        </span>
                      </span>
                    ))}
                  <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-medium">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-gray-300"
                      checked={hideSkippedSettlementRows}
                      onChange={(e) =>
                        setHideSkippedSettlementRows(e.target.checked)
                      }
                    />
                    Hide skipped
                  </label>
                </div>
                <div className="mt-2 max-h-[260px] overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="py-1.5 px-2 text-left">Customer</th>
                        <th className="py-1.5 px-2 text-left">Currency</th>
                        <th className="py-1.5 px-2 text-right">Payout</th>
                        <th className="py-1.5 px-2 text-right">Count</th>
                        <th className="py-1.5 px-2 text-left">Status</th>
                        <th className="py-1.5 px-2 text-left">Transfer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSettlementRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="py-3 px-2 text-center text-gray-500"
                          >
                            {hideSkippedSettlementRows &&
                            settlementSummary.skippedCount > 0
                              ? `All ${settlementSummary.skippedCount} customer mapping(s) had no eligible activity. Uncheck "Hide skipped" to view.`
                              : "No customer mappings returned."}
                          </td>
                        </tr>
                      ) : (
                        visibleSettlementRows.map((row, index) => {
                          const status = (row.status || "").toLowerCase();
                          const isSkipped = status === "skipped";
                          const isError = status.includes("error");
                          return (
                            <tr
                              key={`${row.customerId}-${row.currency}-${index}`}
                              className={`border-b border-gray-100 ${
                                isSkipped
                                  ? "text-gray-500"
                                  : isError
                                  ? "text-red-700"
                                  : ""
                              }`}
                            >
                              <td className="py-1.5 px-2">
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-1.5">
                                    <span
                                      className="font-mono text-[11px]"
                                      title={row.customerId}
                                    >
                                      {truncateUuid(row.customerId)}
                                    </span>
                                    {row.isMe && (
                                      <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                                        you
                                      </span>
                                    )}
                                  </div>
                                  {(row.customerName || row.ownerEmail) && (
                                    <span className="text-[10px] text-gray-500">
                                      {row.customerName || row.ownerEmail || ""}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-1.5 px-2">
                                {row.currency || "-"}
                              </td>
                              <td className="py-1.5 px-2 text-right">
                                {fmtMoney(
                                  row.grossPayoutAmount || 0,
                                  row.currency || "USD"
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-right">
                                {row.conversionCount || 0}
                              </td>
                              <td className="py-1.5 px-2">
                                <span
                                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                    isError
                                      ? "bg-red-100 text-red-700"
                                      : isSkipped
                                      ? "bg-gray-100 text-gray-600"
                                      : status === "submitted"
                                      ? "bg-green-100 text-green-700"
                                      : status === "already_submitted"
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {toTitle(row.status)}
                                </span>
                                {row.stripeError ? (
                                  <SettlementStripeErrorDetail
                                    error={row.stripeError}
                                    fallbackReason={row.reason}
                                  />
                                ) : (
                                  row.reason && (
                                    <span className="ml-1 text-[10px] text-gray-600">
                                      {row.reason}
                                    </span>
                                  )
                                )}
                              </td>
                              <td className="py-1.5 px-2">
                                {row.stripeTransferId ? (
                                  <span
                                    className="font-mono text-[11px]"
                                    title={row.stripeTransferId}
                                  >
                                    {truncateUuid(row.stripeTransferId)}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-medium text-amber-900">
                  Reconcile Unmatched Conversion
                </h4>
                <p className="text-sm text-amber-800">
                  Fix a conversion that exists in Impact but landed as unmatched
                  by linking it to a known affiliate link.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={reconcileActionId}
                  onChange={(e) => setReconcileActionId(e.target.value)}
                  placeholder="Impact action ID"
                  className="px-3 py-2 text-sm border border-amber-300 rounded-md min-w-[260px] bg-white"
                />
                <input
                  value={reconcileAffiliateLinkId}
                  onChange={(e) => setReconcileAffiliateLinkId(e.target.value)}
                  placeholder="Affiliate link ID"
                  className="px-3 py-2 text-sm border border-amber-300 rounded-md min-w-[260px] bg-white"
                />
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                  onClick={reconcileUnmatchedAction}
                  disabled={reconcileLoading}
                >
                  {reconcileLoading ? "Reconciling..." : "Reconcile now"}
                </button>
              </div>
            </div>
            <div className="mt-3 border border-amber-200 rounded-lg overflow-hidden bg-white">
              <div className="p-3 border-b border-amber-200 bg-amber-50 flex items-center justify-between gap-2">
                <h5 className="font-medium text-amber-900">
                  Unmatched Impact Actions
                </h5>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                  <input
                    value={unmatchedSearchInput}
                    onChange={(e) => setUnmatchedSearchInput(e.target.value)}
                    placeholder="Search action ID, reason, payload..."
                    className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[320px] bg-white"
                  />
                </div>
              </div>
              <div className="overflow-auto max-h-[260px]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="py-2 px-3 text-left">Action ID</th>
                      <th className="py-2 px-3 text-left">Status</th>
                      <th className="py-2 px-3 text-right">Sale</th>
                      <th className="py-2 px-3 text-right">Payout</th>
                      <th className="py-2 px-3 text-left">Reason</th>
                      <th className="py-2 px-3 text-left">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedActionsQuery.isLoading ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-center">
                          <Spinner className="!w-[1.25rem] mx-auto" />
                        </td>
                      </tr>
                    ) : unmatchedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-6 text-center text-gray-500"
                        >
                          No unmatched actions found.
                        </td>
                      </tr>
                    ) : (
                      unmatchedRows.map((row) => (
                        <tr
                          key={row.impactActionId}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() =>
                            setReconcileActionId(row.impactActionId)
                          }
                        >
                          <td className="py-2 px-3 font-medium text-indigo-700">
                            {row.impactActionId}
                          </td>
                          <td className="py-2 px-3">{toTitle(row.status)}</td>
                          <td className="py-2 px-3 text-right">
                            {fmtMoney(
                              row.saleAmount || 0,
                              row.currency || "USD"
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {fmtMoney(
                              row.payoutAmount || 0,
                              row.currency || "USD"
                            )}
                          </td>
                          <td className="py-2 px-3 text-gray-700">
                            {row.mismatchReason || "-"}
                          </td>
                          <td className="py-2 px-3">{fmtDate(row.updated)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
                <span className="text-gray-600">{unmatchedPageLabel}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                    disabled={unmatchedOffset === 0}
                    onClick={() =>
                      setUnmatchedOffset((prev) => Math.max(0, prev - LIMIT))
                    }
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                    disabled={!unmatchedActionsQuery.data?.pagination.hasMore}
                    onClick={() => setUnmatchedOffset((prev) => prev + LIMIT)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
              <h4 className="font-medium text-gray-900">
                All Customers Overview
              </h4>
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                <input
                  value={adminSearchInput}
                  onChange={(e) => setAdminSearchInput(e.target.value)}
                  placeholder="Search customer, email, id..."
                  className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[280px] bg-white"
                />
              </div>
            </div>
            <div className="overflow-auto max-h-[360px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleAdminSort("customerName")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Customer
                        {adminSortBy === "customerName" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleAdminSort("ownerEmail")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Owner
                        {adminSortBy === "ownerEmail" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("trackedLinks")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Links
                        {adminSortBy === "trackedLinks" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("clicks")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Clicks
                        {adminSortBy === "clicks" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("conversions")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Conversions
                        {adminSortBy === "conversions" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("approvedConversions")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Approved
                        {adminSortBy === "approvedConversions" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("totalPayoutAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Payout
                        {adminSortBy === "totalPayoutAmount" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adminOverviewQuery.isLoading ? (
                    <tr>
                      <td colSpan={7} className="py-6 text-center">
                        <Spinner className="!w-[1.25rem] mx-auto" />
                      </td>
                    </tr>
                  ) : (adminOverviewQuery.data?.rows || []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-6 text-center text-gray-500"
                      >
                        No customers found.
                      </td>
                    </tr>
                  ) : (
                    (adminOverviewQuery.data?.rows || []).map((row) => (
                      <tr
                        key={row.customerId}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            className="text-indigo-700 hover:underline"
                            onClick={() => {
                              setSelectedCustomerId(row.customerId);
                              setAffiliateViewMode("customer");
                            }}
                          >
                            {row.customerName || row.customerId}
                          </button>
                        </td>
                        <td className="py-2 px-3 text-gray-600">
                          {row.ownerEmail || "-"}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {row.trackedLinks}
                        </td>
                        <td className="py-2 px-3 text-right">{row.clicks}</td>
                        <td className="py-2 px-3 text-right">
                          {row.conversions}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {row.approvedConversions}
                        </td>
                        <td className="py-2 px-3 text-right text-green-700">
                          {fmtMoney(row.totalPayoutAmount || 0)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-600">{adminPageLabel}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={adminOffset === 0}
                  onClick={() =>
                    setAdminOffset((prev) => Math.max(0, prev - LIMIT))
                  }
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={!adminOverviewQuery.data?.pagination.hasMore}
                  onClick={() => setAdminOffset((prev) => prev + LIMIT)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {(affiliateViewMode === "customer" || !canViewAffiliateAdminDashboard) &&
        selectedCustomerId && (
          <Card className="sm:p-4 border border-indigo-200 bg-indigo-50">
            <p className="text-sm text-indigo-800">
              Viewing affiliate data for customer{" "}
              <span className="font-semibold">
                {selectedCustomerOption?.name || "Unnamed Customer"}
              </span>{" "}
              ({selectedCustomerId}).
            </p>
          </Card>
        )}

      {(affiliateViewMode === "customer" ||
        !canViewAffiliateAdminDashboard) && (
        <>
          <div className="grid gap-4 lg:grid-cols-4">
            <Card className="p-4 lg:col-span-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900">
                  Payout Setup
                </h3>
                {payoutStatusQuery.isLoading ? (
                  <Spinner className="!w-[1rem]" />
                ) : payoutStatus?.readyForPayouts ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                    Ready
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    Setup required
                  </span>
                )}
              </div>

              {!payoutStatusQuery.isLoading &&
                (() => {
                  const friendlyRequirements = dedupeFriendlyStripeRequirements(
                    payoutStatus?.requirementsCurrentlyDue
                  );
                  const pendingFriendlyCount = dedupeFriendlyStripeRequirements(
                    payoutStatus?.requirementsPendingVerification
                  ).length;
                  const hasConnect = !!payoutStatus?.hasConnectAccount;
                  const detailsSubmitted = !!payoutStatus?.detailsSubmitted;
                  const requirementsCount = friendlyRequirements.length;

                  let primaryCtaLabel = "Set up payouts";
                  if (payoutCtaLoading === "onboarding") {
                    primaryCtaLabel = "Opening Stripe...";
                  } else if (!hasConnect) {
                    primaryCtaLabel = "Set up payouts";
                  } else if (!detailsSubmitted) {
                    primaryCtaLabel = "Continue setup";
                  } else if (requirementsCount > 0) {
                    primaryCtaLabel = "Continue verification";
                  } else if (payoutStatus?.readyForPayouts) {
                    primaryCtaLabel = "Manage payouts";
                  } else {
                    primaryCtaLabel = "Continue setup";
                  }

                  return (
                    <>
                      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">
                          Pending balance
                        </p>
                        <p className="mt-0.5 text-xl font-semibold text-gray-900">
                          {fmtStripeMinorMoney(
                            payoutStatus?.pendingPayoutAmount || 0,
                            payoutStatus?.pendingPayoutCurrency || "USD"
                          )}
                        </p>
                        {payoutStatus?.lastPayoutAt ? (
                          <p className="text-[11px] text-gray-500 mt-1">
                            Last payout{" "}
                            {fmtStripeMinorMoney(
                              payoutStatus?.lastPayoutAmount,
                              payoutStatus?.lastPayoutCurrency || "USD"
                            )}{" "}
                            on{" "}
                            {fmtDate(
                              new Date(
                                payoutStatus.lastPayoutAt * 1000
                              ).toISOString()
                            )}
                          </p>
                        ) : (
                          <p className="text-[11px] text-gray-500 mt-1">
                            No payouts yet
                          </p>
                        )}
                      </div>

                      {requirementsCount > 0 && (
                        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                          <p className="font-medium">
                            Stripe still needs {requirementsCount} more{" "}
                            {requirementsCount === 1 ? "thing" : "things"} to
                            enable payouts:
                          </p>
                          <ul className="mt-1.5 space-y-1">
                            {friendlyRequirements.slice(0, 5).map((label) => (
                              <li
                                key={label}
                                className="flex items-start gap-2"
                              >
                                <span className="mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-600" />
                                <span>{label}</span>
                              </li>
                            ))}
                            {requirementsCount > 5 && (
                              <li className="pl-3.5 text-[11px] text-amber-800">
                                + {requirementsCount - 5} more (Stripe will show
                                the full list)
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {requirementsCount === 0 &&
                        pendingFriendlyCount > 0 &&
                        !payoutStatus?.readyForPayouts && (
                          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                            <p className="font-medium">
                              Stripe is verifying your information.
                            </p>
                            <p className="mt-0.5">
                              This usually takes 1&ndash;2 business days. We
                              start payouts as soon as it&apos;s approved.
                            </p>
                          </div>
                        )}

                      <div className="mt-3 flex flex-col gap-2">
                        <button
                          type="button"
                          className="inline-flex justify-center items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                          onClick={() => openAffiliatePayoutLink("onboarding")}
                          disabled={payoutCtaLoading !== null}
                        >
                          {primaryCtaLabel}
                        </button>
                        {detailsSubmitted && (
                          <button
                            type="button"
                            className="inline-flex justify-center items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            onClick={() => openAffiliatePayoutLink("dashboard")}
                            disabled={payoutCtaLoading !== null}
                          >
                            {payoutCtaLoading === "dashboard"
                              ? "Opening dashboard..."
                              : "Open Stripe dashboard"}
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
            </Card>

            <Card className="p-0 overflow-hidden lg:col-span-3">
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Payout Ledger
                  </h4>
                  {payoutLedgerQuery.isFetching && (
                    <span className="text-sm text-gray-500">Refreshing...</span>
                  )}
                </div>
              </div>
              <div className="overflow-auto max-h-[360px]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="py-2 px-3 text-left">Period</th>
                      <th className="py-2 px-3 text-right">Net Payout</th>
                      <th className="py-2 px-3 text-right">Conversions</th>
                      <th className="py-2 px-3 text-left">
                        <span className="inline-flex items-center gap-1">
                          Status
                          <InstantTooltip
                            align="start"
                            body="Draft – queued for the next payout run. Submitted – sent from Passio to your Stripe account; Stripe is delivering it to your bank. Paid – the deposit has been delivered to your bank. Failed – something went wrong; we'll retry. Hover any pill below for full details."
                          >
                            <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                          </InstantTooltip>
                        </span>
                      </th>
                      <th className="py-2 px-3 text-left">Stripe Payout</th>
                      <th className="py-2 px-3 text-left">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutLedgerQuery.isLoading ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-center">
                          <Spinner className="!w-[1.25rem] mx-auto" />
                        </td>
                      </tr>
                    ) : payoutLedgerRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-6 text-center text-gray-500"
                        >
                          No payout ledger entries yet.
                        </td>
                      </tr>
                    ) : (
                      payoutLedgerRows.map((row) => {
                        const statusKey = resolvePayoutStatusKey(
                          row.payoutStatus
                        );
                        return (
                          <tr
                            key={row.affiliatePayoutId}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-2 px-3">
                              {fmtDate(row.periodStart)} -{" "}
                              {fmtDate(row.periodEnd)}
                            </td>
                            <td className="py-2 px-3 text-right text-green-700">
                              {fmtMoney(
                                row.netPayoutAmount,
                                row.payoutCurrency
                              )}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {row.sourceConversionCount}
                            </td>
                            <td className="py-2 px-3">
                              <PayoutStatusBadge
                                statusKey={statusKey}
                                align="start"
                              />
                            </td>
                            <td className="py-2 px-3">
                              {row.stripePayoutId ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <span
                                    className="font-mono text-[11px]"
                                    title={row.stripePayoutId}
                                  >
                                    {truncateUuid(row.stripePayoutId)}
                                  </span>
                                  <a
                                    href={stripeConnectTransferDashboardUrl(
                                      row.stripePayoutId
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-indigo-600 hover:text-indigo-800"
                                    title="Open this transfer in the Stripe dashboard"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                  </a>
                                  <CopyButton
                                    value={row.stripePayoutId}
                                    iconOnly
                                    className="!border-0 !bg-transparent !px-0.5 !py-0"
                                  />
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="py-2 px-3">
                              {fmtDate(row.updated)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <details className="group border-t border-gray-200 bg-gray-50">
                <summary className="cursor-pointer select-none list-none flex items-center justify-between gap-2 px-4 py-3 text-sm hover:bg-gray-100 transition-colors">
                  <span className="inline-flex items-center gap-2 font-semibold text-gray-800">
                    <InformationCircleIcon className="h-4 w-4 text-indigo-600" />
                    How your payouts work
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <span className="hidden sm:inline group-open:hidden">
                      Click to expand
                    </span>
                    <span className="hidden group-open:inline">
                      Click to collapse
                    </span>
                    <ChevronDownIcon className="h-4 w-4 transition-transform duration-150 group-open:rotate-180" />
                  </span>
                </summary>
                <div className="px-4 pb-4 pt-1 text-xs leading-relaxed text-gray-700 space-y-3">
                  <p>
                    Your earnings travel from Passio to your bank account in two
                    simple steps. The{" "}
                    <span className="font-semibold">Status</span> column tells
                    you which step a payout is currently in.
                  </p>
                  <ol className="list-decimal pl-5 space-y-1">
                    <li>
                      <span className="font-semibold">
                        Passio sends your earnings to your Stripe account.
                      </span>{" "}
                      As soon as we do this, the row shows{" "}
                      <PayoutStatusBadge
                        statusKey="submitted"
                        size="xs"
                        align="start"
                      />
                      .
                    </li>
                    <li>
                      <span className="font-semibold">
                        Stripe delivers it from your Stripe account to your
                        bank.
                      </span>{" "}
                      This usually takes 1–2 business days. We check
                      automatically, and as soon as Stripe confirms the deposit
                      we flip the row to{" "}
                      <PayoutStatusBadge
                        statusKey="paid"
                        size="xs"
                        align="start"
                      />
                      .
                    </li>
                  </ol>
                  <p className="text-gray-600">
                    First-time payouts may take a few extra days while Stripe
                    verifies your account. After that, payouts are typically
                    near-real-time.
                  </p>
                  <div className="pt-1">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5 font-semibold">
                      Status reference
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(
                        [
                          "submitted",
                          "draft",
                          "paid",
                          "failed",
                          "warning",
                        ] as PayoutStatusKey[]
                      ).map((key) => {
                        const info = PAYOUT_LEDGER_STATUS_INFO[key];
                        return (
                          <div
                            key={key}
                            className="rounded border border-gray-200 bg-white p-2.5"
                          >
                            <div className="mb-1">
                              <PayoutStatusBadge
                                statusKey={key}
                                size="xs"
                                align="start"
                              />
                            </div>
                            <div className="text-[11px] text-gray-600 leading-snug">
                              {info.description}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </details>
            </Card>
          </div>

          <Card className="sm:p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Affiliate Earnings
                </h3>
                <p className="text-sm text-gray-500">
                  Track generated Instacart affiliate links, conversion actions,
                  and earnings attributed to this customer.
                </p>
              </div>
              {(summaryQuery.isFetching ||
                conversionsQuery.isFetching ||
                linksQuery.isFetching) && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Refreshing
                </div>
              )}
            </div>

            {summaryQuery.isLoading ? (
              <div className="py-10 flex justify-center">
                <Spinner className="!w-[2rem]" />
              </div>
            ) : (
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Tracked Links
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {summary?.trackedLinks ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Last link: {fmtDate(summary?.lastTrackedLinkAt)}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Clicks
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {summary?.clicks ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Last click: {fmtDate(summary?.lastClickAt)}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Conversions
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {summary?.conversions ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Last conversion: {fmtDate(summary?.lastConversionAt)}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Total Sales
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-indigo-700">
                    {fmtMoney(summary?.totalSaleAmount || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Approved: {summary?.approvedConversions ?? 0} | Pending:{" "}
                    {summary?.pendingConversions ?? 0}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Total Payout (All Statuses)
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-green-700">
                    {fmtMoney(summary?.totalPayoutAmount || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Reversed: {summary?.reversedConversions ?? 0}
                  </p>
                </Card>
                <Card className="p-4 border border-green-200 bg-green-50">
                  <p className="text-xs uppercase tracking-wide text-green-700">
                    Payout Eligible (Approved + Locked)
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-green-800">
                    {fmtMoney(summary?.eligiblePayoutAmount || 0)}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Eligible conversions: {summary?.eligibleConversions ?? 0}
                  </p>
                </Card>
              </div>
            )}

            {summary && (
              <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="grid gap-3 md:grid-cols-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Sources
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {summary.topSourceTools.length === 0 ? (
                        <span className="text-xs text-gray-500">
                          No data yet
                        </span>
                      ) : (
                        summary.topSourceTools.map((item) => {
                          const isActive =
                            activeAttributionFilter?.type === "source" &&
                            activeAttributionFilter.value === item.key;
                          return (
                            <button
                              key={`${item.key}-${item.count}`}
                              type="button"
                              onClick={() =>
                                isActive
                                  ? clearAttributionFilter()
                                  : applyAttributionFilter("source", item.key)
                              }
                              title={
                                isActive
                                  ? "Clear filter"
                                  : `Filter by source: ${item.key}`
                              }
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition ${
                                isActive
                                  ? "bg-indigo-600 text-white"
                                  : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                              }`}
                            >
                              {toTitle(item.key)}
                              <span
                                className={`text-[10px] ${
                                  isActive
                                    ? "text-indigo-100"
                                    : "text-indigo-500"
                                }`}
                              >
                                {item.count}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Campaigns
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {summary.topCampaigns.length === 0 ? (
                        <span className="text-xs text-gray-500">
                          No data yet
                        </span>
                      ) : (
                        summary.topCampaigns.map((item) => {
                          const isActive =
                            activeAttributionFilter?.type === "campaign" &&
                            activeAttributionFilter.value === item.key;
                          return (
                            <button
                              key={`${item.key}-${item.count}`}
                              type="button"
                              onClick={() =>
                                isActive
                                  ? clearAttributionFilter()
                                  : applyAttributionFilter("campaign", item.key)
                              }
                              title={
                                isActive
                                  ? "Clear filter"
                                  : `Filter by campaign: ${item.key}`
                              }
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition ${
                                isActive
                                  ? "bg-violet-600 text-white"
                                  : "bg-violet-100 text-violet-700 hover:bg-violet-200"
                              }`}
                            >
                              {toTitle(item.key)}
                              <span
                                className={`text-[10px] ${
                                  isActive
                                    ? "text-violet-100"
                                    : "text-violet-500"
                                }`}
                              >
                                {item.count}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Currencies
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {summary.topCurrencies.length === 0 ? (
                        <span className="text-xs text-gray-500">
                          No data yet
                        </span>
                      ) : (
                        summary.topCurrencies.map((item) => (
                          <span
                            key={`${item.currency}-${item.count}`}
                            className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                          >
                            {item.currency}
                            <span className="text-[10px] text-gray-500">
                              {item.count}
                            </span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          <Card className="sm:p-4 border border-indigo-100 bg-indigo-50/30">
            <button
              type="button"
              onClick={() => setDocsExpanded((prev) => !prev)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="rounded-md bg-indigo-100 p-1.5 text-indigo-700">
                  <CodeBracketIcon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    How to Generate Affiliate Links
                  </h3>
                  <p className="text-xs text-gray-600">
                    N-API endpoint, optional params, and a live request builder.
                    Every link is tracked here automatically.
                  </p>
                </div>
              </div>
              <div className="text-gray-500">
                {docsExpanded ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </div>
            </button>

            {docsExpanded && (
              <div className="mt-3 space-y-3">
                <div className="rounded-md border border-indigo-200 bg-white p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                      POST
                    </span>
                    <code className="flex-1 break-all rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[12px] text-gray-800">
                      {passioApiBaseUrl()}
                      {AFFILIATE_LINK_NAPI_PATH}
                    </code>
                    <span className="text-[11px] text-gray-600">
                      Auth:{" "}
                      <code className="rounded bg-gray-100 px-1">
                        Bearer &lt;napi-token&gt;
                      </code>
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] text-gray-600">
                    Body is a JSON array of shopping items{" "}
                    <code>{`{ name, portionQuantity, portionSize }`}</code> (or
                    a full <code>LLMMealPlanResultOutgoing</code> with{" "}
                    <code>shoppingList</code> / <code>mealPlanDays</code>).
                    Response: <code>passio_redirect_url</code> — always send
                    users through that URL.
                  </p>
                </div>

                <div className="rounded-md border border-indigo-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                      Optional params · live builder
                    </h4>
                    <span className="text-[10px] text-gray-500">
                      All optional. Leave blank to omit.
                    </span>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <label className="text-[11px] font-medium text-gray-700">
                      sourceTool
                      <input
                        value={builderSourceTool}
                        onChange={(e) => setBuilderSourceTool(e.target.value)}
                        placeholder="e.g. mealplanner-v2"
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono"
                      />
                      <span className="text-[10px] text-gray-500">
                        → Source column
                      </span>
                    </label>
                    <label className="text-[11px] font-medium text-gray-700">
                      cartId
                      <input
                        value={builderCartId}
                        onChange={(e) => setBuilderCartId(e.target.value)}
                        placeholder="e.g. cart-abc-123"
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono"
                      />
                      <span className="text-[10px] text-gray-500">
                        → Cart column
                      </span>
                    </label>
                    <label className="text-[11px] font-medium text-gray-700">
                      campaign
                      <input
                        value={builderCampaign}
                        onChange={(e) => setBuilderCampaign(e.target.value)}
                        placeholder="e.g. spring-launch"
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono"
                      />
                      <span className="text-[10px] text-gray-500">
                        → Campaign column
                      </span>
                    </label>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Request URL
                    </p>
                    <CopyButton value={builderUrl} label="URL" />
                  </div>
                  <code className="mt-1 block break-all rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-800">
                    {builderUrl}
                  </code>

                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Example curl
                    </p>
                    <CopyButton value={builderCurl} label="curl" />
                  </div>
                  <pre className="mt-1 overflow-x-auto rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-800">
                    {builderCurl}
                  </pre>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Conversion Actions
                  </h4>
                  {activeAttributionFilter && (
                    <button
                      type="button"
                      onClick={clearAttributionFilter}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        activeAttributionFilter.type === "campaign"
                          ? "bg-violet-600 text-white hover:bg-violet-700"
                          : activeAttributionFilter.type === "link"
                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                      title={`Clear filter (${activeAttributionFilter.value})`}
                    >
                      Filtered: {activeAttributionFilter.type} ={" "}
                      {formatAttributionFilterValue(activeAttributionFilter)}
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                    <input
                      value={conversionSearchInput}
                      onChange={(e) => setConversionSearchInput(e.target.value)}
                      placeholder="Search action ID, campaign, cart, link..."
                      className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[280px]"
                    />
                  </div>
                  <select
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    value={conversionStatus}
                    onChange={(e) => {
                      setConversionStatus(e.target.value);
                      setConversionOffset(0);
                    }}
                  >
                    <option value="">All statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="reversed">Reversed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="overflow-auto max-h-[520px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="py-2 px-3 text-left">Action ID</th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleConversionSort("status")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Status
                        {conversionSortBy === "status" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleConversionSort("saleAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Sale
                        {conversionSortBy === "saleAmount" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleConversionSort("payoutAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Payout
                        {conversionSortBy === "payoutAmount" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th className="py-2 px-3 text-left">Link</th>
                    <th className="py-2 px-3 text-left">Source</th>
                    <th className="py-2 px-3 text-left">Campaign</th>
                    <th className="py-2 px-3 text-left">Cart</th>
                    <th className="py-2 px-3 text-left">
                      <span className="inline-flex items-center gap-1">
                        Payout Status
                        <InstantTooltip
                          align="start"
                          body="Shows where this conversion's earnings are in the payout pipeline. Waiting → not yet ready. Eligible → approved and ready, will be sent in the next payout. Submitted/Paid/etc. → already rolled into a payout — hover the pill or scroll down to the Payout Ledger to see details."
                        >
                          <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        </InstantTooltip>
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleConversionSort("actionOccurredAt")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Occurred
                        {conversionSortBy === "actionOccurredAt" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {conversionsQuery.isLoading ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center">
                        <Spinner className="!w-[1.5rem] mx-auto" />
                      </td>
                    </tr>
                  ) : conversionRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="py-8 text-center text-gray-500"
                      >
                        No conversion actions found.
                      </td>
                    </tr>
                  ) : (
                    conversionRows.map((row) => (
                      <tr
                        key={row.impactActionId}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-2 px-3 font-medium text-gray-800">
                          {row.impactActionId}
                        </td>
                        <td className="py-2 px-3">
                          <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                            {toTitle(row.status)}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right">
                          {fmtMoney(row.saleAmount, row.currency || "USD")}
                        </td>
                        <td className="py-2 px-3 text-right text-green-700">
                          {fmtMoney(row.payoutAmount, row.currency || "USD")}
                        </td>
                        <td className="py-2 px-3">
                          {row.impactSubId1 ? (
                            <button
                              type="button"
                              onClick={() =>
                                applyAttributionFilter(
                                  "link",
                                  row.impactSubId1 as string
                                )
                              }
                              className="font-mono text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline"
                              title={`Filter to link ${row.impactSubId1}`}
                            >
                              {truncateUuid(row.impactSubId1)}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-2 px-3">{toTitle(row.sourceTool)}</td>
                        <td className="py-2 px-3">{row.campaign || "-"}</td>
                        <td className="py-2 px-3">{row.cartId || "-"}</td>
                        <td className="py-2 px-3">
                          {renderConversionPayoutStatus(row)}
                        </td>
                        <td className="py-2 px-3">
                          {fmtDate(row.actionOccurredAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-600">{conversionsPageLabel}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={conversionOffset === 0}
                  onClick={() =>
                    setConversionOffset((prev) => Math.max(0, prev - LIMIT))
                  }
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={!conversionsQuery.data?.pagination.hasMore}
                  onClick={() => setConversionOffset((prev) => prev + LIMIT)}
                >
                  Next
                </button>
              </div>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Tracked Affiliate Links
                  </h4>
                  {activeAttributionFilter && (
                    <button
                      type="button"
                      onClick={clearAttributionFilter}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        activeAttributionFilter.type === "campaign"
                          ? "bg-violet-600 text-white hover:bg-violet-700"
                          : activeAttributionFilter.type === "link"
                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                      title={`Clear filter (${activeAttributionFilter.value})`}
                    >
                      Filtered: {activeAttributionFilter.type} ={" "}
                      {formatAttributionFilterValue(activeAttributionFilter)}
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                  <input
                    value={linkSearchInput}
                    onChange={(e) => setLinkSearchInput(e.target.value)}
                    placeholder="Search source, campaign, cart, subId..."
                    className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[280px]"
                  />
                </div>
              </div>
            </div>
            <div className="overflow-auto max-h-[520px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="py-2 px-3 text-left">Sub ID 1</th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleLinkSort("sourceTool")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Source
                        {linkSortBy === "sourceTool" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleLinkSort("campaign")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Campaign
                        {linkSortBy === "campaign" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th className="py-2 px-3 text-left">Cart</th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleLinkSort("clickCount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Clicks
                        {linkSortBy === "clickCount" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleLinkSort("conversionCount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Conversions
                        {linkSortBy === "conversionCount" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleLinkSort("totalPayoutAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Payout
                        {linkSortBy === "totalPayoutAmount" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleLinkSort("created")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Created
                        {linkSortBy === "created" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th className="py-2 px-3 text-right">Redirect URL</th>
                  </tr>
                </thead>
                <tbody>
                  {linksQuery.isLoading ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center">
                        <Spinner className="!w-[1.5rem] mx-auto" />
                      </td>
                    </tr>
                  ) : linkRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-8 text-center text-gray-500"
                      >
                        No tracked links found.
                      </td>
                    </tr>
                  ) : (
                    linkRows.map((row) => {
                      const redirectUrl = `${passioApiBaseUrl()}/v2/products/affiliates/redirect/${
                        row.affiliateLinkId
                      }`;
                      const subId1Display =
                        row.impactSubId1 || row.affiliateLinkId;
                      return (
                        <tr
                          key={row.affiliateLinkId}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-2 px-3 font-medium text-gray-800">
                            <button
                              type="button"
                              onClick={() =>
                                applyAttributionFilter("link", subId1Display)
                              }
                              className="font-mono text-[12px] text-emerald-700 hover:text-emerald-900 hover:underline"
                              title={`Filter to link ${subId1Display}`}
                            >
                              {subId1Display}
                            </button>
                          </td>
                          <td className="py-2 px-3">
                            {toTitle(row.sourceTool)}
                          </td>
                          <td className="py-2 px-3">{row.campaign || "-"}</td>
                          <td className="py-2 px-3">{row.cartId || "-"}</td>
                          <td className="py-2 px-3 text-right">
                            {row.clickCount}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {row.conversionCount}
                          </td>
                          <td className="py-2 px-3 text-right text-green-700">
                            {fmtMoney(row.totalPayoutAmount)}
                          </td>
                          <td className="py-2 px-3">{fmtDate(row.created)}</td>
                          <td className="py-2 px-3 text-right">
                            <CopyButton
                              value={redirectUrl}
                              label="Copy"
                              iconOnly
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-600">{linksPageLabel}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={linkOffset === 0}
                  onClick={() =>
                    setLinkOffset((prev) => Math.max(0, prev - LIMIT))
                  }
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={!linksQuery.data?.pagination.hasMore}
                  onClick={() => setLinkOffset((prev) => prev + LIMIT)}
                >
                  Next
                </button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default AffiliateEarnings;
